import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { X, Loader2, Bot, Plus } from 'lucide-react';
import { apiService } from '../../services/api';
import type { Platform } from '../../types/platform';

interface AddChatbotModalProps {
  isOpen: boolean;
  onClose: () => void;
  platform?: string; // e.g. "facebook"
  onSuccess: (account: Pick<Platform, 'id'>) => void;
}

export const AddChatbotModal: React.FC<AddChatbotModalProps> = ({
  isOpen,
  onClose,
  platform,
  onSuccess,
}) => {
  const { t } = useTranslation('chatbot');
  const [loading, setLoading] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [availablePlatforms, setAvailablePlatforms] = useState<any[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchAvailablePlatforms();
    }
  }, [isOpen]);

  const fetchAvailablePlatforms = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.getAvailablePlatforms();
      setAvailablePlatforms(res || []);
    } catch (err: any) {
      console.error('Failed to fetch available platforms:', err);
      setError('Lỗi khi tải danh sách tài khoản');
    }
    setLoading(false);
  };

  const handleAddPlatform = async (platformId: string) => {
    setAddingId(platformId);
    setError('');
    try {
      const res = await apiService.enableChatbotPlatform(platformId) as any;
      if (res && res.platform) {
        onSuccess({ id: res.platform.id || platformId });
        onClose();
      } else {
        throw new Error('Invalid response');
      }
    } catch (err: any) {
      console.error('Failed to enable chatbot:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Lỗi khi thêm chatbot');
    }
    setAddingId(null);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-0">
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-[16px] font-bold text-gray-900">
              Thêm Chatbot
            </h3>
            <p className="text-[12px] text-gray-400 mt-0.5">
              Chọn tài khoản từ danh sách để bật chế độ chatbot
            </p>
          </div>
          <button
            title={t('close')}
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-100 text-red-600 text-[13px] rounded-xl font-medium">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
              <p className="text-sm text-gray-500">Đang tải danh sách tài khoản...</p>
            </div>
          ) : availablePlatforms.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center gap-3">
              <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center">
                <Bot className="w-6 h-6 text-gray-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Không có tài khoản khả dụng</p>
                <p className="text-[12px] text-gray-500 mt-1 max-w-[250px]">
                  Tất cả tài khoản của bạn đã được bật chatbot, hoặc bạn chưa kết nối tài khoản nào.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3 max-h-[400px] overflow-y-auto pr-1 custom-scrollbar">
              {availablePlatforms.map((p) => {
                const name = p.platform_name || p.name || p.page_id;
                const type = p.platform_type || p.platform || 'facebook';
                const isAdding = addingId === p.id;
                
                return (
                  <div 
                    key={p.id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50/30 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                        {type.includes('facebook') ? (
                          <span className="text-[#1877F2] font-bold text-lg">f</span>
                        ) : type.includes('zalo') ? (
                          <span className="text-[#0068FF] font-bold text-sm">Zalo</span>
                        ) : (
                          <Bot size={20} className="text-gray-400" />
                        )}
                      </div>
                      <div className="min-w-0 pr-2">
                        <p className="text-[14px] font-bold text-gray-900 truncate">
                          {name}
                        </p>
                        <p className="text-[12px] text-gray-500 capitalize">
                          {type.replace('_browser', ' (Cá nhân)')}
                        </p>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => handleAddPlatform(p.id)}
                      disabled={isAdding || addingId !== null}
                      className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg text-[13px] font-semibold transition-colors disabled:opacity-50"
                    >
                      {isAdding ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Plus size={14} />
                      )}
                      Thêm
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
